import { Textarea } from "@nextui-org/react";
import clsx from "clsx";
import debounce from "lodash/debounce";
import {
  ChangeEvent,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { ReviewSourceTypes } from "../../types/index.ts";
import InlineSources from "./InlineSources";

export type FieldValueWithInlineSources = {
  value: string;
  sources: ReviewSourceTypes[];
  isEdited?: boolean;
};

export type AgentEditFieldRef = {
  focus: () => void;
};

type Props = {
  fieldValueObj: FieldValueWithInlineSources;
  title?: string;
  onChange: (value: FieldValueWithInlineSources) => void;
  onSourceClick: (selectedSource: ReviewSourceTypes) => void;
  setListOfSourcesForSelectedSources: (
    selectedSource: ReviewSourceTypes[]
  ) => void;

  noBorder?: boolean;
  noPadding?: boolean;
  expandedView?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  minRows?: number;
  maxRows?: number;
  isDisabled?: boolean;
  focusEnd?: boolean
};

const AgentEditFieldWithInlineSources = forwardRef<AgentEditFieldRef, Props>(
  (props, ref) => {
    const {
      fieldValueObj,
      onChange,
      onSourceClick,
      title,
      noBorder,
      noPadding,
      expandedView,
      autoFocus,
      placeholder,
      minRows,
      maxRows,
      isDisabled,
      setListOfSourcesForSelectedSources,
      focusEnd
    } = props;

    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const cursorPosRef = useRef<number | null>(null);

    useImperativeHandle(ref, () => ({
      focus: () => {
        if (textareaRef.current) {
          textareaRef.current.focus({
            preventScroll: true
          });
          // Set cursor to end of text
          if (focusEnd) {
            const length = textareaRef.current.value.length;
            textareaRef.current.setSelectionRange(length, length);
          }
        }
      },
    }), []);

    const [localValue, setLocalValue] = useState(fieldValueObj.value);
    const [isUserEditing, setIsUserEditing] = useState(false);
    const userEditTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Update local value only when external value changes and user is not editing
    useEffect(() => {
      if (!isUserEditing && fieldValueObj?.value !== localValue) {
        setLocalValue(fieldValueObj.value);
      }
    }, [fieldValueObj?.value, isUserEditing]);

    const debouncedOnChange = useCallback(
      debounce((newValue: FieldValueWithInlineSources) => {
        onChange(newValue);
        setIsUserEditing(false);
      }, 300),
      [onChange]
    );

    useEffect(() => {
      return () => {
        debouncedOnChange.cancel();
        if (userEditTimeoutRef.current) {
          clearTimeout(userEditTimeoutRef.current);
        }
      };
    }, [debouncedOnChange]);

    const handleOnChange = useCallback(
      (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const newValue = e.target.value;
        
        // Update local state immediately for responsive UI
        setLocalValue(newValue);
        setIsUserEditing(true);

        // Clear existing timeout
        if (userEditTimeoutRef.current) {
          clearTimeout(userEditTimeoutRef.current);
        }
        
        // Set a timeout to mark user as not editing if no more changes come
        userEditTimeoutRef.current = setTimeout(() => {
          setIsUserEditing(false);
        }, 400);

        // Save cursor position on user input
        cursorPosRef.current = e.target.selectionStart;

        // Create new field value object and debounce the parent callback
        const newFieldValueObj = {
          ...fieldValueObj,
          value: newValue,
          isEdited: true,
        };

        debouncedOnChange(newFieldValueObj);
      },
      [fieldValueObj, debouncedOnChange]
    );

    // To Prevent textarea scrolling when user add new line
    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
          e.preventDefault();

          const textarea = textareaRef.current;
          if (!textarea) return;

          const start = textarea.selectionStart;
          const end = textarea.selectionEnd;

          const newValueStr =
            localValue.substring(0, start) + "\n" + localValue.substring(end);

          // Update local state immediately
          setLocalValue(newValueStr);
          setIsUserEditing(true);

          // Clear existing timeout
          if (userEditTimeoutRef.current) {
            clearTimeout(userEditTimeoutRef.current);
          }

          // For Enter key, update immediately and set cursor position
          setTimeout(() => {
            if (textarea) {
              textarea.setSelectionRange(start + 1, start + 1);
            }
          }, 0);

          // Create new field value object and call parent onChange immediately for Enter key
          const newFieldValueObj = {
            ...fieldValueObj,
            value: newValueStr,
            isEdited: true,
          };

          onChange(newFieldValueObj);
          setIsUserEditing(false);
        }
      },
      [localValue, fieldValueObj, onChange]
    );

    // Simplified cursor position restoration - only for specific cases
    useEffect(() => {
      const textarea = textareaRef.current;
      if (textarea && cursorPosRef.current !== null && !isUserEditing) {
        requestAnimationFrame(() => {
          if (textarea) {
            textarea.setSelectionRange(cursorPosRef.current!, cursorPosRef.current!);
            cursorPosRef.current = null;
          }
        });
      }
    }, [localValue, isUserEditing]);

    const handleSourceRemoveAtIndex = (index: number) => {
      const newSources = [...(fieldValueObj?.sources ?? [])];
      newSources.splice(index, 1);

      const newFieldValueObj = {
        ...fieldValueObj,
        sources: newSources,
        isEdited: true,
      };

      // Update parent immediately for source removal
      onChange(newFieldValueObj);
    };

    // Check if we should show empty state
    const isEmpty = !localValue || localValue.trim() === '';
    const showEmptyState = isDisabled && isEmpty;

    return (
      <div className="relative w-full">
        {title && (
          <div
            className="bg=[#F9F7F7] text-[#717171] font-medium py-2 px-4 text-[10px] leading-4 "
            style={{
              border: "1px solid #E4E4E7",
              borderLeft: "0px",
              borderRight: "0px",
            }}
          >
            {title}
          </div>
        )}
        {showEmptyState ? (
          <div className="border-[1px] rounded-md px-4 bg-white text-[#A1A1AA] italic text-sm" style={{ paddingTop: '8px', paddingBottom: fieldValueObj?.sources?.length ? '32px' : '8px' }}>
            No data available
          </div>
        ) : (
          <Textarea
            ref={textareaRef}
            className="text-[#121212] font-light bg-white focus-within:text-[#000000] h-fit"
            classNames={{
              base: `shadow-none !border-none rounded-none truncate`,
              inputWrapper: clsx(
                "px-4 py-2 rounded-md border-[1px]",
                noBorder && "border-none",
                noPadding && "p-0",
                fieldValueObj?.sources?.length && "pb-8"
              ),
            }}
            minRows={minRows}
            maxRows={maxRows}
            variant="bordered"
            labelPlacement="outside"
            value={localValue}
            placeholder={placeholder ? placeholder : "Type here..."}
            onChange={handleOnChange}
            onKeyDown={handleKeyDown}
            autoFocus={autoFocus}
            disabled={isDisabled}
            onMouseDown={(e) => {
              const textarea = e.target as HTMLTextAreaElement;
              const clickPosition = textarea.selectionStart;
              textarea.focus({
                preventScroll: true,
              });
              textarea.setSelectionRange(clickPosition, clickPosition);
              
              // Clear any pending user editing timeout when mouse interaction occurs
              if (userEditTimeoutRef.current) {
                clearTimeout(userEditTimeoutRef.current);
              }
            }}
            onFocus={() => {
              // Clear any pending user editing timeout when focused
              if (userEditTimeoutRef.current) {
                clearTimeout(userEditTimeoutRef.current);
              }
            }}
          />
        )}
        {fieldValueObj?.sources && fieldValueObj.sources.length > 0 && (
          <div className={clsx(noPadding ? "p-0" : "px-4")}>
            <InlineSources
              sources={fieldValueObj?.sources}
              onSourceClick={onSourceClick}
              handleSourceRemoveAtIndex={handleSourceRemoveAtIndex}
              setListOfSourcesForSelectedSources={
                setListOfSourcesForSelectedSources
              }
              tooltipPlacement={expandedView ? "bottom" : "bottom"}
            />
          </div>
        )}
      </div>
    );
  }
);

export default AgentEditFieldWithInlineSources;
